// ═══ 말소 탭 `/main/m/malso` (+ `/:id`) — 홈과 분리(Jin 10/27) · 앱 malso_list_screen.dart ═══
// 제목 + 기간(월 — 행 날짜 기준: 완료 건은 완료일, 그 외 신청일) → 칩 4 → 목록. 홈 3칸·더 보기에서
// `?filter=active|needsAction|done&ym=YYYY-MM` 으로 들어오고, 칩·기간을 바꾸면 주소에도 남긴다(replace —
// 상세에 다녀오거나 새로고침해도 그대로).
// ≥1024 는 목록 | 상세 2-pane — routes 가 `/m/malso/:id` 도 이 화면을 렌더하고(ListOrDetail) 우측에 MalsoDetailBody.
// ≤1023 은 1단, 행 클릭 = 상세 페이지로 이동. 집계·필터·정렬은 homeLogic.js(앱과 같은 정의).
// 데이터: listRequests + myDocRequests('요청') → certFlags(완료 id ≤200). 소켓 cancel:step·cancel-doc:* → 재조회.
// 대행사(listMalsoClients isBroker): 칩 줄 끝 '거래처: 전체 ▾' — 고르면 그 거래처(효과 단위 unitId) 행으로 buildHome 다시
// (칩 개수·기간도 그 기준, 주소엔 남기지 않음). 하위 회사: 대행사가 넣은 행이 있으면 목록 아래 안내 한 줄(시안 SubList).
// 실패·구 서버 → 비대행사 취급(현행 화면).
// props: { user, showToast }
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { T, R } from '../../theme';
import { useT, useLocale } from '../../../i18n/I18nProvider';
import { getSocket } from '../../../utils/socket';
import { listRequests, myDocRequests, certFlags, listMalsoClients } from '../api';
import { withKoJosa } from '../koJosa';
import { buildHome, certIdsOf, visibleRows, needsAction, certArrived, countsOf, monthsOf } from '../homeLogic';
import { currentMonth, monthLabel } from '../../simple/fmt';
import { describeSimpleError } from '../../simple/errors';
import { useIsDesktop } from '../../simple/useDesktop';
import ErrorText from '../../simple/parts/ErrorText';
import GroupChips from '../../simple/screens/requests/GroupChips';
import MalsoRow from '../parts/MalsoRow';
import { MalsoDetailBody } from './MalsoDetail';

var CHIPS = [
  { id: 'all', labelKey: 'cancelHistoryMonthAll' },
  { id: 'active', labelKey: 'malsoModeStripActive' },
  { id: 'needsAction', labelKey: 'malsoModeStripNeedsAction', tone: 'amber' },
  { id: 'done', labelKey: 'cancelHistoryFilterDone' }
];
var CHIP_IDS = { all: 1, active: 1, needsAction: 1, done: 1 };
var SOCKET_EVENTS = ['cancel:step', 'cancel-doc:requested', 'cancel-doc:message', 'cancel-doc:cancelled'];

var headRow = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 };
var title = { fontSize: 19, fontWeight: 800, letterSpacing: '-.35px', color: T.navy, margin: 0 };
var periodWrap = { display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap', justifyContent: 'flex-end' };
var periodNote = { fontSize: 11.5, color: T.faint };
var periodSel = { height: 34, maxWidth: '100%', padding: '0 10px', borderRadius: R.pill, border: '1px solid ' + T.bd,
  background: T.card, color: T.ink, fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' };
var periodSelOn = Object.assign({}, periodSel, { border: '1px solid ' + T.accentBd, background: T.accentTint, color: T.accentPress });
var unitSel = Object.assign({}, periodSelOn, { height: 29, fontSize: 12 });
var chipLine = { display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' };
var subInfo = { fontSize: 12.5, color: T.sub, lineHeight: 1.55 };
var paneInfo = { padding: '10px 14px' };
var col = { display: 'flex', flexDirection: 'column', gap: 10 };
var card = { background: T.card, border: '1px solid ' + T.bd, borderRadius: R.card, overflow: 'clip' };
// 데스크톱 좌 패널 머리(칩) — SimpleRequests `lhStyle` 와 같은 sticky 머리.
var paneHead = { position: 'sticky', top: 0, zIndex: 1, display: 'flex', flexDirection: 'column', gap: 9,
  padding: '11px 14px', borderBottom: '1px solid ' + T.bd, background: T.head };
var paneEmpty = { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 18px',
  textAlign: 'center', fontSize: 13, color: T.muted };
var empty = { padding: '48px 18px', textAlign: 'center' };
var emptyTitle = { fontSize: 14, fontWeight: 800, color: T.ink };
var emptySub = { fontSize: 12.5, color: T.muted, marginTop: 6, lineHeight: 1.6 };
var loadingStyle = { padding: '40px 0', textAlign: 'center', fontSize: 12.5, color: T.muted };

/// 주소 query → { filter, ym }. 모르는 칩은 전체, 형식이 틀린 기간은 전체 기간.
function readQuery(search) {
  var q = new URLSearchParams(search || '');
  var f = q.get('filter');
  var ym = q.get('ym');
  return { filter: CHIP_IDS[f] ? f : 'all', ym: /^\d{4}-\d{2}$/.test(ym || '') ? ym : '' };
}

/// { filter, ym } → '?filter=…&ym=…' (기본값은 생략).
function toQuery(filter, ym) {
  var q = new URLSearchParams();
  if (filter && filter !== 'all') q.set('filter', filter);
  if (ym) q.set('ym', ym);
  var s = q.toString();
  return s ? '?' + s : '';
}

function MalsoList(props) {
  var t = useT();
  var loc = useLocale();
  var navigate = useNavigate();
  var location = useLocation();
  var isDesktop = useIsDesktop();
  var id = useParams().id;
  var stH = useState(null); var src = stH[0], setSrc = stH[1];   // { rows, docs, flags, clients }
  var stU = useState(0); var unitId = stU[0], setUnitId = stU[1];
  var stE = useState(null); var err = stE[0], setErr = stE[1];
  // 처음 한 번만 주소에서 읽는다 — 데스크톱 행 클릭(/m/malso/:id)으로 주소가 바뀌어도 고른 값은 화면 상태가 진실.
  var init = useState(function () { return readQuery(location.search); })[0];
  var stF = useState(init.filter); var filter = stF[0], setFilterState = stF[1];
  var stY = useState(init.ym); var ym = stY[0], setYmState = stY[1];

  var load = useCallback(function () {
    var noBroker = function () { return { isBroker: false }; };
    return Promise.all([listRequests(), myDocRequests('요청'), listMalsoClients().catch(noBroker)]).then(function (res) {
      var rows = Array.isArray(res[0]) ? res[0] : [];
      return certFlags(certIdsOf(rows)).then(function (body) {
        setSrc({ rows: rows, docs: res[1], flags: body && body.flags, clients: (res[2] && res[2].isBroker) ? res[2] : noBroker() });
        setErr(null);
      });
    }).catch(function (e) { setErr(e); });
  // setter 2종은 useState 가 보장하는 안정 참조 — 배열 구조분해라 린터가 못 알아본다.
  }, [setSrc, setErr]);

  var broker = !!(src && src.clients.isBroker);
  var home = useMemo(function () {
    if (!src) return null;
    var rows = broker && unitId ? src.rows.filter(function (r) { return Number(r.unitId) === unitId; }) : src.rows;
    return buildHome(rows, src.docs, src.flags, currentMonth());
  }, [src, broker, unitId]);

  useEffect(function () { load(); }, [load]);

  useEffect(function () {
    var sock = getSocket(); if (!sock) return undefined;
    var on = function () { load(); };
    SOCKET_EVENTS.forEach(function (ev) { sock.on(ev, on); });
    return function () { try { SOCKET_EVENTS.forEach(function (ev) { sock.off(ev, on); }); } catch (e) { /* 이미 끊김 */ } };
  }, [load]);

  // 칩·기간을 바꾸면 주소에도 남긴다(replace — 뒤로가기 기록은 늘리지 않는다).
  var keep = function (f, y) { navigate(location.pathname + toQuery(f, y), { replace: true }); };
  var setFilter = function (f) { setFilterState(f); keep(f, ym); };
  var setYm = function (y) { setYmState(y); keep(filter, y); };

  var list = home ? visibleRows(home, filter, ym) : [];
  var counts = home ? countsOf(home, ym) : null;
  var selId = id ? Number(id) : 0;
  var selRow = home && selId ? home.rows.filter(function (r) { return Number(r.id) === selId; })[0] : undefined;
  var chips = (<GroupChips value={filter} onChange={setFilter} items={CHIPS.map(function (c) {
    return { id: c.id, tone: c.tone, label: t(c.labelKey), count: counts ? counts[c.id] : 0 };
  })} />);
  if (broker) {
    chips = (<div style={chipLine}>{chips}
      <select data-testid="malso-unit" aria-label={t('malsoModeBrokerClients')} style={unitSel} value={unitId}
        onChange={function (e) { setUnitId(Number(e.target.value) || 0); }}>
        <option value={0}>{t('malsoModeBrokerFilterChip', { name: t('cancelHistoryMonthAll') })}</option>
        {src.clients.clients.map(function (c) {
          var name = c.isSelf ? t('malsoModeBrokerSelfName', { name: c.name }) : c.name;
          return <option key={c.id} value={c.id}>{t('malsoModeBrokerFilterChip', { name: name })}</option>;
        })}
      </select>
    </div>);
  }
  // 하위 회사 — 대행사가 이 회사 거래처로 넣은 행(requestedByMe false)이 있으면 안내 한 줄.
  var byOther = !broker && src ? src.rows.filter(function (r) { return r.requestedByMe === false && r.requesterCompanyName; })[0] : null;
  var subInfoEl = byOther ? (<div data-testid="malso-sub-info" style={subInfo}>{t('malsoModeBrokerSubInfo', {
    broker: withKoJosa(loc.locale, byOther.requesterCompanyName, '이', '가'), unit: byOther.unitName || '' })}</div>) : null;
  var period = home ? (<label style={periodWrap}>
    <span style={periodNote}>{t('malsoModePeriodBasis')}</span>
    <select data-testid="malso-period" aria-label={t('filterSheetPeriodTitle')} style={ym ? periodSelOn : periodSel}
      value={ym} onChange={function (e) { setYm(e.target.value); }}>
      <option value="">{t('malsoModePeriodAll') + ' (' + home.rows.length + ')'}</option>
      {monthsOf(home).map(function (m) {
        return <option key={m.ym} value={m.ym}>{monthLabel(loc.locale, m.ym) + ' (' + m.count + ')'}</option>;
      })}
      {/* 홈에서 고른 달에 건이 없어도 선택값은 보이게 */}
      {ym && !monthsOf(home).some(function (m) { return m.ym === ym; })
        ? <option value={ym}>{monthLabel(loc.locale, ym) + ' (0)'}</option> : null}
    </select>
  </label>) : null;
  var errEl = err ? <ErrorText>{describeSimpleError(t, err)}</ErrorText> : null;
  var rowsEl = !home ? (err ? null : <div style={loadingStyle}>{t('commonLoading')}</div>)
    : list.length ? list.map(function (r) {
      return (<MalsoRow key={r.id} row={r} needsAction={needsAction(home, r)} certArrived={certArrived(home, r)}
        selected={isDesktop && Number(r.id) === selId}
        onClick={function () { navigate('/main/m/malso/' + r.id + toQuery(filter, ym)); }} />);
    }) : (<div style={empty}>
      <div style={emptyTitle}>{t('malsoModeEmptyTitle')}</div>
      {home.rows.length ? null : <div style={emptySub}>{t('malsoModeEmptyBody')}</div>}
    </div>);

  return (<div className="ss-malso">
    <div style={headRow}>
      <h2 style={title}>{t('malsoModeTabMalso')}</h2>
      {period}
    </div>
    {isDesktop ? (<div className="ss-2col">
      <div className="ss-list-pane">
        <div style={paneHead}>{chips}</div>
        {errEl}
        {rowsEl}
        {subInfoEl ? <div style={paneInfo}>{subInfoEl}</div> : null}
      </div>
      <div className="ss-detail-pane">
        {id ? <MalsoDetailBody key={id} id={id} row={selRow} user={props.user} showToast={props.showToast} onChanged={load} />
          : <div style={paneEmpty}>{t('malsoModeWebPickOne')}</div>}
      </div>
    </div>) : (<div style={col}>
      {chips}
      {errEl}
      {rowsEl ? <div style={card}>{rowsEl}</div> : null}
      {subInfoEl}
    </div>)}
  </div>);
}

export default MalsoList;
